import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PlaceIcon from "@mui/icons-material/Place";
import EventIcon from "@mui/icons-material/Event";
import GroupIcon from "@mui/icons-material/Group";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Nav from "../NavBar/Nav";
import FooterNew from "../NavBar/FooterNew";
import { useExperienceCart } from "./ExperienceCartContext";
import { formatMoney, formatDisplayDate } from "./experienceUtils";
import "./Experiences.css";

// Small self-contained image carousel for a single cart line — its own
// active-slide state so paging one item's photos never touches any other
// item in the list.
function CartItemImage({ images, title }) {
  const [active, setActive] = useState(0);
  const gallery = images && images.length ? images : [];
  const canSlide = gallery.length > 1;

  const goPrev = (e) => {
    e.stopPropagation();
    setActive((i) => (i === 0 ? gallery.length - 1 : i - 1));
  };
  const goNext = (e) => {
    e.stopPropagation();
    setActive((i) => (i === gallery.length - 1 ? 0 : i + 1));
  };

  return (
    <div className="exp-cart-item-image">
      {gallery.length ? (
        <img src={gallery[active]} alt={title} />
      ) : (
        <div className="exp-product-image-placeholder" style={{ position: "static", height: "100%" }} />
      )}
      {canSlide && (
        <>
          <button
            type="button"
            className="exp-card-arrow exp-card-arrow-prev exp-cart-arrow"
            onClick={goPrev}
            aria-label="Previous image"
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="exp-card-arrow exp-card-arrow-next exp-cart-arrow"
            onClick={goNext}
            aria-label="Next image"
          >
            <ChevronRightIcon fontSize="small" />
          </button>
          <div className="exp-card-dots exp-cart-dots">
            {gallery.map((_, idx) => (
              <span
                key={idx}
                className={`exp-card-dot${idx === active ? " exp-card-dot-active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActive(idx);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function ExperienceCart() {
  const navigate = useNavigate();
  const { items, subtotal, removeItem, updateItem } = useExperienceCart();

  const currency = items.length ? items[0].currency : "USD";

  return (
    <div className="exp-scope">
      <div style={{ position: "relative" }}>
        <Nav />
      </div>

      <div className="exp-container">
        <div className="exp-page-heading">
          <h1>Your cart</h1>
          <p>Review your experiences before checking out — nothing is booked yet.</p>
        </div>

        <div className="exp-cart-body">
          <div>
            {items.length === 0 && (
              <div className="exp-cart-empty">
                <h3>Your cart is empty</h3>
                <p>Browse tours and experiences to start planning your trip.</p>
                <a className="exp-inline-btn" onClick={() => navigate("/experiences")}>
                  Find experiences
                </a>
              </div>
            )}

            {items.map((item) => (
              <div className="exp-cart-item" key={item.lineId}>
                <CartItemImage
                  images={item.images && item.images.length ? item.images : item.image ? [item.image] : []}
                  title={item.title}
                />
                <div>
                  <div className="exp-cart-item-title">{item.title}</div>
                  <div className="exp-cart-item-meta">
                    {item.destinationName && (
                      <span>
                        <PlaceIcon fontSize="inherit" style={{ verticalAlign: "-2px" }} />{" "}
                        {item.destinationName}
                      </span>
                    )}
                    {item.date && (
                      <span>
                        <EventIcon fontSize="inherit" style={{ verticalAlign: "-2px" }} />{" "}
                        {formatDisplayDate(item.date)}
                      </span>
                    )}
                    <span className="exp-mini-stepper">
                      <GroupIcon fontSize="inherit" />
                      <button
                        className="exp-stepper-btn"
                        onClick={() =>
                          updateItem(item.lineId, { travelers: Math.max(1, item.travelers - 1) })
                        }
                        disabled={item.travelers <= 1}
                      >
                        −
                      </button>
                      {item.travelers}
                      <button
                        className="exp-stepper-btn"
                        onClick={() =>
                          updateItem(item.lineId, { travelers: Math.min(20, item.travelers + 1) })
                        }
                      >
                        +
                      </button>
                    </span>
                  </div>
                </div>
                <div className="exp-cart-item-controls">
                  <div className="exp-cart-item-price">
                    {formatMoney(item.price * item.travelers, item.currency)}
                  </div>
                  <button className="exp-remove-link" onClick={() => removeItem(item.lineId)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {items.length > 0 && (
            <div className="exp-summary-card">
              <h3>Order summary</h3>
              <div className="exp-summary-row">
                <span>
                  {items.length} experience{items.length > 1 ? "s" : ""}
                </span>
                <span>{formatMoney(subtotal, currency)}</span>
              </div>
              <div className="exp-summary-row">
                <span>Taxes &amp; fees</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="exp-summary-total">
                <span>Total</span>
                <span>{formatMoney(subtotal, currency)}</span>
              </div>
              <button className="exp-btn-primary" onClick={() => navigate("/experiences/checkout")}>
                Proceed to checkout
              </button>
              <button className="exp-btn-secondary" onClick={() => navigate("/experiences")}>
                Add more experiences
              </button>
            </div>
          )}
        </div>
      </div>

      <FooterNew />
    </div>
  );
}
